import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, AlertTriangle, Info } from "lucide-react";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { usePermissions } from "components/permissions/usePermissions";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { createClipConfig, quoteClip } from "data/mutations-common";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import {
  ClipConfigProps,
  ClipStepsControlProps,
  hasPremiumData,
} from "../../clip-menu";
import { ClipConfiguration, CRS } from "../../models";
import {
  DEFAULT_FORMAT,
  FORMAT_LABELS,
  PublishFormat,
} from "../../format-labels";
import { validateClipName } from "../../validation";
import { DataTree } from "./data-tree";
import { CardTitle } from "@/components/ui/card";

export const FinaliseClip = (
  props: ClipConfigProps & ClipStepsControlProps,
) => {
  const [identityState] = useIdentity();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { isPermitted } = usePermissions();
  const [nameError, setNameError] = useState<string | null>(null);

  const identityId = resolveIdentityId(identityState);

  useEffect(() => {
    if (props.clipConfig.name) {
      const error = validateClipName(props.clipConfig.name);
      setNameError(error);
    }
  }, [props.clipConfig.name]);

  // Check if clip contains premium data and if user has permission
  const clipHasPremiumData = hasPremiumData(props.clipConfig);
  const hasPremiumDataPermission = isPermitted([
    PermissionsKeys.premiumDataCreate,
  ]);
  const showPremiumWarning = clipHasPremiumData && !hasPremiumDataPermission;

  const isReadOnly = () => {
    return (
      props.clipConfig.status == "Quoting" ||
      props.clipConfig.status == "Processing" ||
      props.clipConfig.status == "Completed"
    );
  };

  const { enqueueMutationFailed } = useCustomSnackbars();

  const clipsQuoteMutation = useMutation({
    mutationFn: (clipId: string) => quoteClip(clipId),
    onSuccess: (clipConfig: ClipConfiguration) => {
      props.setClipConfig((prev: ClipConfiguration) => {
        return {
          ...prev,
          status: clipConfig.status,
        };
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.clipsKey, identityId],
      });
      enqueueSnackbar("Clip has been submitted for quoting.", {
        variant: "success",
      });
      props.resetClipConfig();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });

  const clipQueryKey = [QueryKeys.clipsKey, identityId];

  const saveAndQuoteMutation = useMutation({
    mutationFn: (clipConfig: ClipConfiguration) => createClipConfig(clipConfig),
    onSuccess: () => {
      clipsQuoteMutation.mutate(props.clipConfig.id);
    },
    onMutate: async (newConfig) => {
      await queryClient.cancelQueries({ queryKey: clipQueryKey });

      const previousConfig = queryClient.getQueriesData({
        queryKey: clipQueryKey,
      });

      queryClient.setQueriesData({ queryKey: clipQueryKey }, () => {
        newConfig;
      });

      return { previousConfig };
    },
    onError: (error: Error, newConfig, context) => {
      queryClient.setQueriesData(
        { queryKey: clipQueryKey },
        context?.previousConfig,
      );
      enqueueMutationFailed(error.toString());
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [clipQueryKey] });
    },
  });

  const onQuoteSubmit = () => {
    saveAndQuoteMutation.mutate(props.clipConfig);
  };

  const isLoading =
    clipsQuoteMutation.isPending || saveAndQuoteMutation.isPending;

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto overflow-x-hidden">
      <CardTitle>Finalise Clip</CardTitle>
      {/* Says why every field is locked: `isReadOnly()` disables the form once
          the clip leaves Draft, which otherwise reads as a broken screen. */}
      {props.clipConfig.status === "Quoting" && (
        <Alert>
          <Loader2 className="size-4 animate-spin" />
          <AlertDescription>
            Quoting your clip, you will be notified when it's ready
          </AlertDescription>
        </Alert>
      )}
      {props.clipConfig.status === "Processing" && (
        <Alert>
          <Info className="size-4" />
          <AlertDescription>
            Clip Processing, you will be notified when it's ready
          </AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col overflow-auto gap-2">
        <div className="flex flex-col gap-1">
          <Label htmlFor="finalise-name">Name</Label>
          <Input
            id="finalise-name"
            disabled={isReadOnly()}
            value={props.clipConfig.name}
            maxLength={200}
            onChange={(event) => {
              const newName = event.target.value;
              props.setClipConfig((prev: ClipConfiguration) => ({
                ...prev,
                name: newName,
              }));
              const error = validateClipName(newName);
              setNameError(error);
            }}
            className={nameError ? "border-destructive" : ""}
          />
          {nameError && <p className="text-xs text-destructive">{nameError}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="output-format">Format</Label>
          <Select
            disabled={isReadOnly()}
            value={props.clipConfig.format ?? DEFAULT_FORMAT}
            onValueChange={(value) =>
              props.setClipConfig((prev: ClipConfiguration) => ({
                ...prev,
                format: value as PublishFormat,
              }))
            }
          >
            <SelectTrigger id="output-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-2000">
              {(Object.keys(FORMAT_LABELS) as PublishFormat[]).map((value) => (
                <SelectItem key={value} value={value}>
                  {FORMAT_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="output-crs">Coordinate Reference System</Label>
          <Select
            disabled={isReadOnly()}
            value={props.clipConfig.crs ?? "EPSG:4283"}
            onValueChange={(value) =>
              props.setClipConfig((prev: ClipConfiguration) => ({
                ...prev,
                crs: value as CRS,
              }))
            }
          >
            <SelectTrigger id="output-crs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-2000">
              <SelectItem value="EPSG:7844">GDA2020 (EPSG:7844)</SelectItem>
              <SelectItem value="EPSG:4283">GDA94 (EPSG:4283)</SelectItem>
              <SelectItem value="EPSG:3857">
                WGS 84 / Pseudo-Mercator (EPSG:3857)
              </SelectItem>
              <SelectItem value="EPSG:4326">WGS 84 (EPSG:4326)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="client-ref">Client Reference</Label>
          <Input
            id="client-ref"
            disabled={isReadOnly()}
            value={props.clipConfig.clientReference}
            maxLength={200}
            onChange={(event) =>
              props.setClipConfig((prev: ClipConfiguration) => ({
                ...prev,
                clientReference: event.target.value,
              }))
            }
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            disabled={isReadOnly()}
            rows={3}
            value={props.clipConfig.notes}
            maxLength={200}
            onChange={(event) =>
              props.setClipConfig((prev: ClipConfiguration) => ({
                ...prev,
                notes: event.target.value,
              }))
            }
          />
        </div>
        <p className="text-sm font-medium">Boundary</p>
        <div className="flex flex-wrap gap-1">
          {props.clipConfig.geometry?.features.map((feature, idx: number) => (
            <span key={`chip-${idx}`}>
              {feature.properties && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline">
                      {feature.properties["name"]}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>{feature.properties["name"]}</TooltipContent>
                </Tooltip>
              )}
            </span>
          ))}
        </div>
        <p className="text-sm font-medium">Data</p>
        <DataTree selected={props.clipConfig.attributes ?? []} />
        {showPremiumWarning && (
          <Alert variant="info">
            <AlertTriangle className="size-4" />
            <AlertTitle>This clip contains premium data</AlertTitle>
            <AlertDescription>
              You can request a quote, but to purchase it you will need to
              upgrade to the Team plan.
            </AlertDescription>
          </Alert>
        )}
      </div>
      <div className="self-end mt-auto flex gap-2">
        <Button
          variant="ghost"
          disabled={isReadOnly() || !!nameError}
          onClick={() => props.setActiveStep(Math.max(props.activeStep - 1, 0))}
        >
          Back
        </Button>
        <Button
          id="quote-clip"
          disabled={
            !props.clipConfig.name || !!nameError || isLoading || isReadOnly()
          }
          onClick={() => onQuoteSubmit()}
        >
          {isLoading && <Loader2 className="size-4 mr-2 animate-spin" />}
          Quote
        </Button>
      </div>
    </div>
  );
};
